import {
  collection,
  doc,
  query,
  orderBy,
  where,
  serverTimestamp,
  setDoc,
  updateDoc,
  type DocumentData,
  type FirestoreDataConverter,
  type Query,
  type WithFieldValue,
  Timestamp,
} from 'firebase/firestore';

import { firestore } from './config';
import { USE_API, api, apiQuery } from '@/services/api/client';

export type TipoNotificacion = 'reponer_producto' | 'mencion' | 'sistema';

export interface Notificacion {
  id: string;
  paraRol: 'admin' | 'doctor' | 'recepcion' | 'all';
  tipo: TipoNotificacion;
  titulo: string;
  mensaje: string;
  productoId?: string;
  productoNombre?: string;
  origen?: 'venta' | 'interno';
  creadaPorUid: string;
  creadaPorNombre: string;
  leida: boolean;
  creadaEn: Timestamp;
}

const conv: FirestoreDataConverter<Notificacion> = {
  toFirestore(d: WithFieldValue<Notificacion>): DocumentData {
    return d as DocumentData;
  },
  fromFirestore(snap, options) {
    const data = snap.data(options) as Omit<Notificacion, 'id'>;
    return { id: snap.id, ...data };
  },
};

export const notificacionesCollection = collection(firestore, 'notificaciones').withConverter(conv);

export function notificacionesPara(rol: 'admin' | 'doctor' | 'recepcion'): any {
  if (USE_API) return apiQuery('/notificaciones', { paraRol_in: `${rol},all`, _order: '-creada_en' });
  return query(
    notificacionesCollection,
    where('paraRol', 'in', [rol, 'all']),
    orderBy('creadaEn', 'desc'),
  );
}

export interface CrearNotifReponerInput {
  productoId: string;
  productoNombre: string;
  origen: 'venta' | 'interno';
  cantidadSugerida?: number;
  notaExtra?: string;
  creadaPorUid: string;
  creadaPorNombre: string;
}

export async function crearNotifReponer(input: CrearNotifReponerInput) {
  const payload = {
    paraRol: 'admin',
    tipo: 'reponer_producto',
    titulo: `Reposición pedida: ${input.productoNombre}`,
    mensaje:
      `${input.creadaPorNombre} pidió reponer ${input.productoNombre}` +
      (input.cantidadSugerida ? ` (sugerencia: ${input.cantidadSugerida} unidades)` : '') +
      (input.notaExtra ? `. Nota: ${input.notaExtra}` : ''),
    productoId: input.productoId,
    productoNombre: input.productoNombre,
    origen: input.origen,
    creadaPorUid: input.creadaPorUid,
    creadaPorNombre: input.creadaPorNombre,
    leida: false,
  };
  if (USE_API) {
    await api.post('/notificaciones', payload);
    return;
  }
  const id = doc(notificacionesCollection).id;
  await setDoc(doc(notificacionesCollection, id), { ...payload, creadaEn: serverTimestamp() } as any);
}

export async function marcarLeida(id: string) {
  if (USE_API) return void (await api.patch(`/notificaciones/${id}`, { leida: true }));
  await updateDoc(doc(notificacionesCollection, id), { leida: true });
}

export async function marcarTodasLeidas(notifs: Notificacion[]) {
  await Promise.all(notifs.filter((n) => !n.leida).map((n) => marcarLeida(n.id)));
}
